import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faClock,
  faSpinner,
  faTriangleExclamation,
  faRocket,
  faCopy,
  faXmark,
  faDownload,
  faFileLines,
  faGaugeHigh,
  faRotateRight
} from '@fortawesome/free-solid-svg-icons';

import toast from 'react-hot-toast';
import { dashboardApi, migrationsApi } from '../services/api';
import { Card, Badge, Button, PageHeader } from '../components/ui';

/*
|--------------------------------------------------------------------------
| Dashboard Page
|--------------------------------------------------------------------------
| Overview of all Domo -> Power BI migration jobs: status counts up top,
| a recent-migrations table below with per-row log viewing and result
| download. Styled with the same Card/Badge/Button primitives and CSS
| variables used across the rest of the app (Projects, Assess, Discovery)
| instead of the previous one-off dark modal styling.
|--------------------------------------------------------------------------
*/

export default function DashboardPage() {
  const navigate = useNavigate();
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const { data: statsData, isLoading, refetch } = useQuery(
    'dashboard-stats',
    () => dashboardApi.stats(),
    { refetchInterval: 5000 }
  );

  const retryMutation = useMutation(
    jobId => migrationsApi.retry(jobId),
    {
      onSuccess: () => {
        toast.success('Migration retry started!');
        refetch();
      },
      onError: () => toast.error('Failed to retry migration'),
    }
  );

  const stats = statsData?.data || {};
  const jobs = stats?.jobs || [];
  function downloadConversionResult(jobId) {
    dashboardApi.downloadResult(jobId)
      .then(res => {
        const disposition = res.headers['content-disposition'];
        let fileName = `migration_result_${jobId}`;

        if (disposition && disposition.includes('filename=')) {
          const fileNameMatch = disposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch?.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => {
        console.error('Download error:', err);
        alert('Failed to download file');
      });
  }

  const copyLogsToClipboard = () => {
    const tableText = [
      ['Time', 'Level', 'Message'].join('\t'),
      ...selectedLogs.map(log => {
        const parsed = typeof log === 'string' ? JSON.parse(log) : log;
        return [
          formatTime(parsed.timestamp),
          parsed.level?.toUpperCase() || 'INFO',
          parsed.message || '',
        ].join('\t');
      }),
    ].join('\n');

    try {
      const textarea = document.createElement('textarea');
      textarea.value = tableText;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const statCards = [
    { label: 'Completed', value: stats?.data?.done || 0, icon: faCircleCheck, color: 'var(--success)', bg: 'var(--success-bg)' },
    { label: 'Queued', value: stats?.data?.queued || 0, icon: faClock, color: 'var(--info)', bg: 'var(--info-bg)' },
    { label: 'In Progress', value: stats?.data?.running || 0, icon: faSpinner, color: 'var(--warning-text)', bg: 'var(--warning-bg)', spin: true },
    { label: 'Failed', value: stats?.data?.failed || 0, icon: faTriangleExclamation, color: 'var(--danger)', bg: 'var(--danger-bg)' },
  ];
  
  return (
    <div style={{ padding: 24 }} className="animate-fade-in">

      {/* HEADER */}
      <PageHeader
        title="Migration Dashboard"
        subtitle="Overview of all Domo → Power BI migrations"
        actions={
          <Button variant="primary" onClick={() => navigate('/migration')}>
            <FontAwesomeIcon icon={faRocket} /> Start Migration
          </Button>
        }
      />

      {/* STAT CARDS */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 14,
          marginBottom: 22,
        }}
      >
        {statCards.map(stat => (
          <div
            key={stat.label}
            style={{
              background: 'var(--bg-card)',
              border: '0.5px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: '18px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              boxShadow: 'var(--shadow-sm)',
              transition: 'box-shadow .15s, transform .15s',
            }}
            onMouseOver={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={e => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 'var(--radius-md)',
                background: stat.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={stat.icon} spin={stat.spin} style={{ color: stat.color, fontSize: 17 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* RECENT MIGRATIONS TABLE */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '16px 20px',
            borderBottom: '0.5px solid var(--border)',
          }}
        >
          <FontAwesomeIcon icon={faGaugeHigh} style={{ color: 'var(--accent)' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Recent Migrations
          </div>
          <Badge variant="neutral" style={{ marginLeft: 4 }}>{jobs.length}</Badge>
        </div>

        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            Loading migrations...
          </div>
        ) : !jobs.length ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              No migrations yet.
            </div>
            <Button variant="primary" onClick={() => navigate('/migration')}>
              <FontAwesomeIcon icon={faRocket} /> Start your first migration
            </Button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Report Name', 'Dataset', 'Status', 'Logs', 'Download', 'Retry'].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '10px 16px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        borderBottom: '0.5px solid var(--border)',
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map((row, i) => (
                  <tr
                    key={row.jobId || i}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={dashCellStyle}>{row?.config?.reportName || '-'}</td>
                    <td style={dashCellStyle}>{row?.config?.datasetName || '-'}</td>
                    <td style={dashCellStyle}>
                      <Badge
                        variant={
                          row.status === 'done' ? 'success'
                            : row.status === 'running' ? 'warning'
                            : row.status === 'queued' ? 'info'
                            : 'danger'
                        }
                      >
                        {row.status}
                      </Badge>
                    </td>
                    <td style={dashCellStyle}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSelectedLogs(row?.logs || []);
                          setShowLogsModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faFileLines} /> View Logs
                      </Button>
                    </td>
                    <td style={dashCellStyle}>
                      {row.status === 'done' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => downloadConversionResult(row.jobId)}
                        >
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </Button>
                      ) : row.status === 'failed' ? (
                        <span style={{ color: 'var(--danger)', fontSize: 12 }}>No file (migration failed)</span>
                      ) : (
                        <span style={{ color: 'var(--warning-text)', fontSize: 12 }}>Not available until done</span>
                      )}
                    </td>
                    <td style={dashCellStyle}>
                      {(row.status === 'failed' || row.status === 'done') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => retryMutation.mutate(row.jobId)}
                          loading={retryMutation.isLoading}
                        >
                          <FontAwesomeIcon icon={faRotateRight} /> Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* LOGS MODAL */}
      {showLogsModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Execution logs"
          onMouseDown={e => { if (e.target === e.currentTarget) setShowLogsModal(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            zIndex: 1000,
            padding: 24,
          }}
        >
          <div
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-lg)',
              width: 760,
              maxWidth: '95%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '0.5px solid var(--border)',
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                Execution Logs
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={copyLogsToClipboard} title="Copy logs">
                  <FontAwesomeIcon icon={faCopy} /> Copy
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setShowLogsModal(false)} title="Close">
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </div>
            </div>

            <div style={{ padding: '8px 0', overflowY: 'auto', flex: 1 }}>
              {selectedLogs.length === 0 ? (
                <div style={{ color: 'var(--text-tertiary)', padding: '24px 20px', fontSize: 13 }}>
                  No logs available
                </div>
              ) : (
                selectedLogs.map((log, i) => {
                  const parsed = typeof log === 'string' ? JSON.parse(log) : log;
                  const logStyle = getLogStyle(parsed.level);

                  return (
                    <div
                      key={i}
                      style={{
                        padding: '9px 20px',
                        borderBottom: '0.5px solid var(--border)',
                        fontSize: 12,
                        fontFamily: 'var(--font-mono)',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span style={{ width: 90, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                        [{formatTime(parsed.timestamp)}]
                      </span>
                      <span style={{ color: logStyle.color }}>{getIcon(parsed.level)}</span>
                      <span
                        style={{
                          flex: 1,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {parsed.message}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* COPY TOAST */}
      {showCopied && (
        <div
          style={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            padding: '10px 16px',
            borderRadius: 'var(--radius-md)',
            border: '0.5px solid var(--border)',
            fontSize: 13,
            boxShadow: 'var(--shadow-md)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <FontAwesomeIcon icon={faCircleCheck} style={{ color: 'var(--success)' }} />
          Logs copied successfully
        </div>
      )}

    </div>
  );
}

const dashCellStyle = {
  padding: '10px 16px',
  borderBottom: '0.5px solid var(--border)',
  color: 'var(--text-primary)',
};

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString();
};

const getLogStyle = (level) => {
  switch (level) {
    case 'error':
      return { color: 'var(--danger)' };
    case 'warn':
      return { color: 'var(--warning-text)' };
    case 'success':
      return { color: 'var(--success)' };
    default:
      return { color: 'var(--info)' };
  }
};

const getIcon = (level) => {
  switch (level) {
    case 'error':
      return '✗';
    case 'warn':
      return '⚠';
    case 'success':
      return '✓';
    default:
      return 'ℹ';
  }
};
